import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { getOfficeEducation, getStateWorkerClassification } from '~/actions';
import { useUserSelector } from '~/hooks/useUserSelector';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';
import { SoftwareUtils } from '~/utils/SoftwareUtils';
import type { ISignUpFormValues } from '../types';
import { TIME_PER_PATIENT_OPTIONS } from '~/constants/officeProfileOptions';

// An office that reached this step before GOTU-8249 can have "N/A" stored.
// InputSelectField shows the placeholder for a value that isn't in `options`
// while react-hook-form keeps holding it, so hydrating a retired value leaves
// a field that looks unanswered but still satisfies `required` and re-submits
// "N/A". Leaving it empty makes the user pick a real duration.
const TIME_PER_PATIENT_VALUES = new Set(TIME_PER_PATIENT_OPTIONS.map(({ value }) => value));
const isTimePerPatient = (value?: string | null): value is string =>
  !!value && TIME_PER_PATIENT_VALUES.has(value);

interface IUserLike {
  contact_number?: string | null;
  office_info?: {
    timePerPatientRDH?: string | null;
    timePerPatientDA?: string | null;
    timePerPatientDN?: string | null;
    isAssistedHygiene?: boolean | null;
  } | null;
  // parkingInformation lives at the user root (the shape the account page reads).
  parkingInformation?: {
    isParkingFree?: boolean | null;
    cost?: number | null;
    costType?: string | null;
    parkingType?: string | null;
  } | null;
}

interface IUserCredentialsResponse {
  education?: { softwares?: string[] | null };
  insurances?: string[] | null;
}

export const useOfficeSetupHydration = () => {
  const dispatch = useDispatch();
  const user = useUserSelector() as IUserLike | null | undefined;
  const { setValue } = useFormContext<ISignUpFormValues>();

  useEffect(() => {
    if (!user || !user.contact_number?.trim()) return;

    dispatch(getOfficeEducation());
    dispatch(getStateWorkerClassification());

    const info = user?.office_info;
    if (isTimePerPatient(info?.timePerPatientRDH)) {
      setValue('timePerPatient.rdh', info.timePerPatientRDH);
    }
    if (isTimePerPatient(info?.timePerPatientDA)) {
      setValue('timePerPatient.da', info.timePerPatientDA);
    }
    if (isTimePerPatient(info?.timePerPatientDN)) {
      setValue('timePerPatient.ddn', info.timePerPatientDN);
    }
    if (typeof info?.isAssistedHygiene === 'boolean') {
      setValue('assistedHygiene', info.isAssistedHygiene ? 'yes' : 'no');
    }

    const p = user?.parkingInformation;
    if (p?.parkingType) setValue('parkingInfo.type', p.parkingType);
    if (typeof p?.isParkingFree === 'boolean') {
      setValue('parkingInfo.isFree', p.isParkingFree ? 'yes' : 'no');
    }
    if (typeof p?.cost === 'number') setValue('parkingInfo.cost', String(p.cost));
    if (p?.costType === 'hr' || p?.costType === 'day') {
      setValue('parkingInfo.unit', p.costType);
    }

    // Softwares + insurances live on /credential-service/user/credentials
    // (same endpoint OfficeDetailsForm uses for the user's specialty).
    apiMethods
      .userCredentialsRequest()
      .then((data: IUserCredentialsResponse | undefined) => {
        dispatch({ type: apiMethodsConst.USER_CREDENTIALS_REQUEST, payload: data });
        const savedSoftwares = data?.education?.softwares ?? [];
        const savedInsurances = data?.insurances ?? [];
        if (savedSoftwares.length) {
          setValue('softwares', SoftwareUtils.getSoftwareTranslations(savedSoftwares));
        }
        if (savedInsurances.length) {
          setValue('insurances', savedInsurances);
        }
      })
      .catch(() => {
        // Non-blocking — user can re-pick.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
